<script lang="ts">
import { defineComponent } from "vue";
import { Role } from "@/constants/navigation";
import { useAppStore } from "@/store/app.store";
import { useChatStore } from "@/store/chat.store";
import type { DispatcherPermissionValue } from "@/api/dto/dispatcher.dto";

export default defineComponent({
  data() {
    return {
      avatarColor(thread: any) {
        // in a chat modal window, while loading thread name is null.
        if (thread.resolved || !thread.name || thread.archived) {
          return "#BDBDBD";
        }

        const colors = ["#9575CD", "#EF5350", "#7986CB", "#4FC3F7", "#81C784", "#FFD54F", "#FF8A65"];
        return colors[
          thread.name
            .split("")
            .reduce((previousValue: number, currentLetter: string) => previousValue + currentLetter.charCodeAt(0), 0) %
            7
        ];
      },
      roles: [
        { id: "ad", name: this.$t("dispatchers.role-admin") },
        { id: "bk", name: this.$t("dispatchers.role-booking") },
        { id: "hr", name: this.$t("dispatchers.role-hr") },
        { id: "op", name: this.$t("dispatchers.role-operation") },
        { id: "ac", name: this.$t("dispatchers.role-accounting") },
      ],
      params: { page: 1, pageSize: 10 },
      pagination: { itemsPerPage: [10, 25, 50, 100] },
    };
  },
  methods: {
    getPreviewPath(file: any) {
      return "/api/v1/files/" + file.previewPath;
    },
    hasStandardTimezone(value: string) {
      const dateChunks = value.split(" ");
      return ["EDT", "EST"].indexOf(dateChunks[1]) > -1;
    },
    getAssetUrl(path: string) {
      return "https://hugohunter.fra1.digitaloceanspaces.com/" + path;
    },
    async loadDispatchers() {
      try {
        // page_size = 0 means we load all dispatchers
        const resp = await this.$api.dispatchers.findDispatchers({ pageSize: 0 });
        const appStore = useAppStore();
        appStore.setDispatchers((resp.data as any).items);
      } catch (err) {
        console.error("Error load account info", err);
      }
    },
    arrayToMap(arr: (object & { id: string | number; name: string })[]): { [key: string | number]: string } {
      const map: { [key: string | number]: string } = {};
      for (const item of arr) {
        map[item.id] = item.name;
      }
      return map;
    },
    hasAccess(requiredPermission: DispatcherPermissionValue | DispatcherPermissionValue[]) {
      const appStore = useAppStore();

      const dispatcher = appStore.dispatcher;

      if (!dispatcher) {
        return false;
      }

      const isAdmin = dispatcher.isSuperAdmin || dispatcher.roles.includes(Role.admin);

      if (isAdmin) {
        return true;
      }

      const permissions = Array.isArray(requiredPermission) ? requiredPermission : [requiredPermission];

      if (!permissions.length) {
        return true;
      }

      return permissions.some((permission) => dispatcher.permissions.includes(permission));
    },
    hasRole(role: Role | Role[]) {
      const appStore = useAppStore();
      if (typeof role == "string") role = [role];
      return role.some((r) => appStore.dispatcher?.roles.includes(r));
    },
    clearDigits(value: string) {
      return value.replace(/[^.\d]/g, "");
    },
    setTitle(title: string, title2: string | null = null, showInHeader = false) {
      const appStore = useAppStore();
      appStore.setTitle(title);

      if (title2) {
        title = title + " – " + title2;
      }

      if (showInHeader) {
        appStore.setHeaderTitle(title);
      } else {
        appStore.setHeaderTitle("");
      }
      let account = "";
      if (appStore.dispatcher?.accountName) {
        account = " – " + appStore.dispatcher?.accountName;
      }
      document.title = title + account + " – TMS";
    },
    showChat(user: any) {
      const chatStore = useChatStore();
      chatStore.setChatUser(user);
    },
    transformUpperCase(e: any) {
      e.target.value = e.target.value.toUpperCase();
    },
    transformLowerCase(e: any) {
      e.target.value = e.target.value.toLowerCase();
    },
    async refresh() {
      // Placeholder for the refresh method
    },
    async updatePage(page: number) {
      this.params.page = page;
      await this.refresh();
    },
    async updatePageSize(size: number) {
      this.params.pageSize = size;
      await this.refresh();
    },
    debounce(func: (...args: any[]) => void, timeout = 300) {
      let timer: ReturnType<typeof setTimeout>;
      return (...args: any[]) => {
        clearTimeout(timer);
        timer = setTimeout(() => {
          func.apply(this, args);
        }, timeout);
      };
    },
    scrollToError() {
      const element = document.querySelector(".v-field--error:first-of-type");
      element?.scrollIntoView({ behavior: "smooth", block: "center" });
    },
    uniqueArray(objects: any[], uniqueBy: string[], keepFirst = true) {
      return Array.from(
        objects
          .reduce((map, e) => {
            const key = uniqueBy
              .map((key) => [e[key], typeof e[key]])
              .flat()
              .join("-");
            if (keepFirst && map.has(key)) return map;
            return map.set(key, e);
          }, new Map())
          .values(),
      );
    },
  },
});
</script>
